import React from "react"
import PropTypes from "prop-types"
import * as Sentry from "@sentry/browser"

import { thankable } from "QuorumGrassroots/widgets/ReusableComponents/WidgetWrappers/ThankableWrapper"

import { resetSubmittedWidget } from "QuorumGrassroots/widgets/action-creators"
import { selectWidgetSubmitted } from "QuorumGrassroots/widgets/selectors"
import { selectCampaignThanksProps } from "QuorumGrassroots/widgets/Campaign/selectors"
import { StyledContrastText } from "QuorumGrassroots/styled-components/components/StyledContrastText"

import { CampaignWarning } from "QuorumGrassroots/widgets/Campaign/components/CampaignWarning"

/**
 * Detects inconsistencies where user should be able to participate but is being blocked
 * and logs to Sentry only in these problematic cases
 */
const detectAndLogParticipationInconsistency = (props) => {
    const {
        canParticipate,
        campaignTargetsSupporter,
        hasAlreadyParticipated,
        campaign,
        hasLoaded,
        isLoadingContent,
        campaignListLoading,
    } = props
    const userdata = window.userdata

    if (!hasLoaded || isLoadingContent || campaignListLoading || !campaign) {
        return
    }

    const allowsMultipleActions = campaign.multiple_actions_allowed

    const isActiveCampaign = campaign.status === DjangIO.app.grassroots.campaign.types.CampaignStatus.active.value

    // Campaign targets supporter but hasAlreadyParticipated is inconsistent
    // Backend says can participate but frontend blocks
    const shouldBeAbleToParticipate =
        campaignTargetsSupporter && isActiveCampaign && (!hasAlreadyParticipated || allowsMultipleActions)

    if (shouldBeAbleToParticipate) {
        // Detected inconsistency
        const participatedCampaigns = userdata?.participated_campaigns || []
        const campaignId = campaign.id

        try {
            Sentry.captureMessage("Campaign Participation Inconsistency Detected", {
                level: "warning",
                tags: {
                    component: "BaseCampaignFormWrapper",
                    issue_type: "participation_inconsistency",
                },
                extra: {
                    campaign_id: campaignId,
                    multiple_actions_allowed: allowsMultipleActions,
                    canParticipateReduxSelector: canParticipate,
                    can_participate_in_campaign: campaign._extra?.can_participate_in_campaign,
                    campaign_targets_supporter: campaignTargetsSupporter,
                    has_already_participated: hasAlreadyParticipated,
                    participated_campaigns: participatedCampaigns,
                    participated_campaigns_includes_current: participatedCampaigns.includes(campaignId),
                    userdata_has_ever_participated: userdata?.has_ever_participated,
                    supporter_id: userdata?.id,
                    organization_id: userdata?.organization_id,
                    timestamp: new Date().toISOString(),
                    url: window.location.href,
                    window_userdata_participated_campaigns: window.userdata?.participated_campaigns,
                    localStorage_userdata_exists: !!localStorage.getItem("userdata"),
                    sessionStorage_userdata_exists: !!sessionStorage.getItem("userdata"),
                },
            })
        } catch (sentryError) {
            console.warn("Failed to log to Sentry:", sentryError)
            console.warn("Campaign Participation Inconsistency:", {
                campaignId,
                shouldBeAbleToParticipate,
                canParticipate,
                can_participate_in_campaign: campaign._extra?.can_participate_in_campaign,
                campaignTargetsSupporter,
                hasAlreadyParticipated,
                participatedCampaigns,
            })
        }
    }
}

export const WrappedCampaign = ({ t, ...props }) => {
    if (!props.hasLoaded || props.isLoadingContent || props.campaignListLoading) {
        return <StyledContrastText isCampaignPage>{t("loading")}</StyledContrastText>
    } else if (!props.canParticipate) {
        // Detect participation inconsistencies and log to Sentry if necessary
        detectAndLogParticipationInconsistency(props)

        const text =
            props.hasAlreadyParticipated && props.campaignTargetsSupporter
                ? t("campaign.cannot_participate_again")
                : t("campaign.cannot_participate")

        return (
            <CampaignWarning
                className="cannot-participate-banner"
                text={text}
                dataCy="cannot-participate"
                t={t}
                isCampaignPage
                campaign={props.campaign}
            />
        )
    } else {
        const CampaignForm = props.campaignForm

        return (
            <div className="base-campaign-form-wrapper">
                <CampaignForm {...props} t={t} />
            </div>
        )
    }
}

WrappedCampaign.propTypes = {
    campaignForm: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    hasLoaded: PropTypes.bool.isRequired,
    isLoadingContent: PropTypes.bool.isRequired,
    canParticipate: PropTypes.bool.isRequired,
    campaignListLoading: PropTypes.bool.isRequired,
    hasAlreadyParticipated: PropTypes.bool.isRequired,
    campaignTargetsSupporter: PropTypes.bool,
    campaign: PropTypes.object,
    userdata: PropTypes.object,
}

/**
 * This is the wrapper that all forms must have. This is responsible for
 * doing all the logic checks about whether or not we should show the form,
 * and also the thankable wrapper.
 *
 * This is the wrapper that controls what the user sees AFTER the user has
 * logged in.
 */
export const baseCampaignFormWrapper = (CampaignForm) => {
    const component = (props) => <WrappedCampaign {...props} campaignForm={CampaignForm} />

    return thankable({
        shouldThankSelector: selectWidgetSubmitted,
        thanksPropsSelector: selectCampaignThanksProps,
        onRerouteThank: resetSubmittedWidget,
    })(component)
}

/**
 * Since we are gradually moving campaigns out of redux, we are redirecting to the thank you page
 * manually on each campaign, so we don't need the thankable wrapper.
 */
export const baseCampaignFormWrapperWithoutThankable = (CampaignComponent) => {
    return (props) => <WrappedCampaign {...props} campaignForm={CampaignComponent} />
}
